import axios, { type AxiosRequestConfig, type Method } from 'axios';
import { _config } from 'Utilities/envConfig';
import * as log from 'Utilities/log';
import mediator, { type MediatorRequest } from 'Utilities/mediator';
import { getAuthSession } from 'Utilities/session';
import { getObject } from 'Utilities/storage';

// NOTE: replaced fetch with axios which is more standard and
// not plagued with some of the cross browser issues with fetch
const _invoke = <T>(
  method: Method,
  url: AxiosRequestConfig['url'],
  params: AxiosRequestConfig['params'],
  data?: T,
  auth?: { username: string; password: string },
  headers?: AxiosRequestConfig['headers']
) =>
  new Promise((resolve, reject) => {
    const request: AxiosRequestConfig<T> = {
      method,
      url,
      data,
      params,
      headers: {
        'Cache-control': 'no-cache',
        'Content-type': 'application/json',
        'Access-Control-Allow-Origin': '*',
        Pragma: 'no-cache',
        'Sharing-Web-Version': import.meta.env.PUBLIC_VERSION ?? '',
        ...headers,
      },
      auth,
      timeout: 30000, // 30s
    };
    const secret = getObject<string>('NEWYU_OPEN');

    if (secret && request.headers) {
      request.headers['newyu-open'] = secret;
    }

    const authTicket = getAuthSession();

    if (authTicket && request.headers) {
      request.headers.Authorization = `Bearer ${authTicket.access_token}`;
    }

    if (authTicket && request?.headers?.BasicAuth) {
      request.headers.Authorization = `Bearer ${authTicket.access_token},Basic ${request?.headers?.BasicAuth}`;
      delete request?.headers?.BasicAuth;
    }

    axios(request)
      .then((response) => {
        if (log.getLogLevel() === 'DEBUG') {
          log.groupAndTrace(
            `API: response for ${url}`,
            ['request', log.cleanObjectProperties(request)],
            ['response', response]
          );
        }

        // data is the body of the response
        // status is the status
        // statusText = "OK"
        // headers = {}
        // config - data used for request
        // request - the XMLHTTPRequest
        resolve(response);
      })
      .catch((error) => {
        if (log.getLogLevel() === 'DEBUG') {
          log.groupAndTrace(
            `API: Error response for ${url}`,
            ['request', log.cleanObjectProperties(request)],
            ['response', error]
          );
        }

        if (error.response) {
          reject({ status: error.response.status, ...error });

          return;
        }

        reject({ status: 500, code: error.code });
      });
  });

const _handleInvoke = <T = unknown>(request: MediatorRequest<T>) => {
  if (log.getLogLevel() === 'DEBUG') {
    log.debug(
      `API: incomingRequest: "${request.url}"`,
      String(log.cleanObjectPropertiesToString(request))
    );
  }

  if (!request) {
    throw new Error('missing Request');
  }

  const url = 'url' in request && typeof request.url === 'string' ? request.url : '';

  const requestUrl = _config.mock ? url.replace('MOCK', '') : url;

  // validate the request
  try {
    if ((!('method' in request) && !request.method) || typeof request.method !== 'string') {
      throw new Error('missing method(GET,POST,PUT,DELETE)');
    }

    if (!request.url) {
      throw new Error('missing url(users/create)');
    }

    if ('headers' in request && request.headers && typeof request.headers !== 'object') {
      throw new Error('headers must be an object');
    }

    request.response = _invoke<T>(
      request.method as Method,
      requestUrl,
      request.params,
      request.data,
      request.auth,
      request.headers as object | undefined
    );
  } catch (ex) {
    request.response = Promise.reject(ex);
  }
};

mediator.subscribe('api:invoke', _handleInvoke);
